// NEW_FILE_JUSTIFIED: New hook for multi-lead extraction state management — distinct from single-lead extraction in GmailPage.tsx. Manages useReducer state for the extractLeadsFromThread action flow.
import { useCallback, useReducer } from 'react';
import { useAction } from 'convex/react';
import { api } from '@convex/_generated/api';
import { toast } from 'sonner';
import type { IExtractedLeadWithStatus, IUseExtractedLeadsReturn } from '@/@types/leads';

// LOCAL_TYPE_JUSTIFIED: Reducer state is internal to this hook, not shared across components
interface IExtractedLeadsState {
  leads: IExtractedLeadWithStatus[];
  isExtracting: boolean;
  isCreating: boolean;
  gmailThreadId: string | null;
  dialogOpen: boolean;
  createdLeadIds: Map<number, string>;
}

// LOCAL_TYPE_JUSTIFIED: Reducer actions are internal to this hook
type TExtractedLeadsAction =
  | { type: 'START_EXTRACTION' }
  | { type: 'EXTRACTION_SUCCESS'; leads: IExtractedLeadWithStatus[]; threadId: string }
  | { type: 'EXTRACTION_ERROR' }
  | { type: 'CLOSE_DIALOG' }
  | { type: 'SET_CREATING'; value: boolean }
  | { type: 'LEAD_CREATED'; index: number; leadId: string };

const initialState: IExtractedLeadsState = {
  leads: [],
  isExtracting: false,
  isCreating: false,
  gmailThreadId: null,
  dialogOpen: false,
  createdLeadIds: new Map(),
};

function reducer(state: IExtractedLeadsState, action: TExtractedLeadsAction): IExtractedLeadsState {
  switch (action.type) {
    case 'START_EXTRACTION':
      return { ...initialState, isExtracting: true };
    case 'EXTRACTION_SUCCESS':
      return {
        ...state,
        isExtracting: false,
        leads: action.leads,
        gmailThreadId: action.threadId,
        dialogOpen: true,
      };
    case 'EXTRACTION_ERROR':
      return { ...state, isExtracting: false };
    case 'CLOSE_DIALOG':
      return initialState;
    case 'SET_CREATING':
      return { ...state, isCreating: action.value };
    case 'LEAD_CREATED': {
      const newMap = new Map(state.createdLeadIds);
      newMap.set(action.index, action.leadId);
      const updatedLeads = state.leads.map((lead, i) =>
        i === action.index
          ? { ...lead, existingLead: { id: action.leadId, name: lead.leadData.name } }
          : lead
      );
      return { ...state, leads: updatedLeads, createdLeadIds: newMap };
    }
    default:
      return state;
  }
}

export function useExtractedLeads(): IUseExtractedLeadsReturn {
  const [state, dispatch] = useReducer(reducer, initialState);
  const extractAction = useAction(api.leads.aiLeadExtraction.extractLeadsFromThread);

  const extractFromThread = useCallback(
    async (thread: {
      threadId: string;
      subject: string;
      messages: Array<{
        from: string;
        bodyText?: string;
        snippet: string;
        direction: 'incoming' | 'outgoing';
      }>;
    }) => {
      dispatch({ type: 'START_EXTRACTION' });

      try {
        const result = await extractAction({
          threadId: thread.threadId,
          subject: thread.subject,
          messages: thread.messages.map(m => ({
            from: m.from,
            bodyText: m.bodyText,
            snippet: m.snippet ?? '',
          })),
          defaultCountryCode: '+357',
        });

        if (result.leads.length === 0) {
          toast.info('No leads found in this thread');
          dispatch({ type: 'EXTRACTION_ERROR' });
          return;
        }

        // LOCAL_TYPE_JUSTIFIED: Inline type for action result item — matches extractLeadsFromThread return shape, not shared elsewhere
        type TExtractedLeadResult = (typeof result.leads)[number];
        const leads: IExtractedLeadWithStatus[] = result.leads.map(
          (lead: TExtractedLeadResult) => ({
            leadData: {
              name: lead.name,
              email: lead.email,
              phoneNumber: lead.phoneNumber,
              leadType: lead.leadType,
              temperature: lead.temperature,
              serviceInterest: lead.serviceInterest,
              estimatedValue: lead.estimatedValue,
              source: lead.source,
              notes: lead.notes,
              matchedServices: lead.matchedServices,
              unmatchedServices: lead.unmatchedServices,
            },
            existingLead: lead.existingLead
              ? { id: lead.existingLead.id, name: lead.existingLead.name }
              : null,
          })
        );

        toast.success(`Found ${leads.length} lead${leads.length > 1 ? 's' : ''} in thread`);
        dispatch({ type: 'EXTRACTION_SUCCESS', leads, threadId: thread.threadId });
      } catch (error) {
        console.error('Multi-lead extraction failed:', error);
        toast.error('Failed to extract leads from thread');
        dispatch({ type: 'EXTRACTION_ERROR' });
      }
    },
    [extractAction]
  );

  const closeDialog = useCallback(() => {
    dispatch({ type: 'CLOSE_DIALOG' });
  }, []);

  const onLeadCreated = useCallback((index: number, leadId: string) => {
    dispatch({ type: 'LEAD_CREATED', index, leadId });
  }, []);

  return {
    extractedLeads: state.leads,
    isExtracting: state.isExtracting,
    isCreating: state.isCreating,
    gmailThreadId: state.gmailThreadId,
    dialogOpen: state.dialogOpen,
    closeDialog,
    extractFromThread,
    onLeadCreated,
  };
}
